import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useOrgName, getClientId, isDailyLimit, DAILY_LIMIT_MSG } from "@/lib/saathiClient";
import { VoiceCall } from "@/components/VoiceCall";
import { Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Languages ──
const LANGUAGES = [
  { code: "hi", label: "हिंदी", sub: "Hindi", flag: "🇮🇳", rtl: false },
  { code: "pa", label: "ਪੰਜਾਬੀ", sub: "Punjabi", flag: "🇮🇳", rtl: false },
  { code: "ur", label: "اردو", sub: "Urdu", flag: "🇵🇰", rtl: true },
  { code: "ta", label: "தமிழ்", sub: "Tamil", flag: "🇱🇰", rtl: false },
  { code: "bn", label: "বাংলা", sub: "Bengali", flag: "🇧🇩", rtl: false },
  { code: "gu", label: "ગુજરાતી", sub: "Gujarati", flag: "🇮🇳", rtl: false },
  { code: "tl", label: "Filipino", sub: "Tagalog", flag: "🇵🇭", rtl: false },
  { code: "ar", label: "العربية", sub: "Arabic", flag: "🇸🇦", rtl: true },
  { code: "en", label: "English", sub: "English", flag: "🇨🇦", rtl: false },
];

// ── Per-language UI copy ──
const COPY = {
  hi: {
    greeting: "नमस्ते! मैं साथी हूँ — कनाडा में आपका दोस्त।",
    intro: "स्वास्थ्य, सरकारी चिट्ठियाँ, किरायेदार के अधिकार, टैक्स, स्कूल — जो भी हो, बताइए। मैं आपकी बात सुनने के लिए यहाँ हूँ।",
    placeholder: "बताइए, मन में क्या है…",
    name_q: "पहले बताइए, आपका नाम क्या है?",
    wa_q: "अपना WhatsApp नंबर दीजिए — जब हमारी इंसानी टीम तैयार होगी तो हम संपर्क करेंगे।",
    time_q: "आपसे बात करने का सबसे अच्छा समय कौन सा है?",
    thanks_title: "शुक्रिया",
    name_ph: "आपका नाम…",
    continue: "आगे बढ़ें",
    step_of: "कदम",
    of: "में से",
    talk_to_person: "वेटलिस्ट पर जुड़ें",
    back_chat: "चैट पर वापस जाएँ",
    nudge_title: "इंसानी मदद चाहिए?",
    nudge_body: "अभी सिर्फ़ Saathi (AI) है। हम एक असली इंसान वाली सेवा बना रहे हैं — वेटलिस्ट पर जुड़िए, तैयार होते ही हम संपर्क करेंगे।",
    nudge_cta: "वेटलिस्ट पर जोड़ें",
    morning: "🌅 सुबह (9am–12pm)",
    afternoon: "☀️ दोपहर (12–5pm)",
    evening: "🌙 शाम (5–9pm)",
  },
  pa: {
    greeting: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ! ਮੈਂ ਸਾਥੀ ਹਾਂ — ਕੈਨੇਡਾ ਵਿੱਚ ਤੁਹਾਡਾ ਦੋਸਤ।",
    intro: "ਸਿਹਤ, ਸਰਕਾਰੀ ਚਿੱਠੀਆਂ, ਕਿਰਾਏਦਾਰ ਦੇ ਹੱਕ, ਟੈਕਸ, ਸਕੂਲ — ਜੋ ਵੀ ਹੋਵੇ, ਦੱਸੋ। ਮੈਂ ਸੁਣਨ ਲਈ ਇੱਥੇ ਹਾਂ।",
    placeholder: "ਦੱਸੋ, ਮਨ ਵਿੱਚ ਕੀ ਹੈ…",
    name_q: "ਤੁਹਾਡਾ ਨਾਮ ਕੀ ਹੈ?",
    wa_q: "ਆਪਣਾ WhatsApp ਨੰਬਰ ਦਿਓ — ਜਦੋਂ ਅਸਾਡੀ ਇਨਸਾਨੀ ਟੀਮ ਤਿਆਰ ਹੋਵੇਗੀ ਤਾਂ ਸੰਪਰਕ ਕਰਾਂਗੇ।",
    time_q: "ਸਾਡੇ ਨਾਲ ਗੱਲ ਕਰਨ ਦਾ ਸਭ ਤੋਂ ਵਧੀਆ ਸਮਾਂ ਕਦੋਂ ਹੈ?",
    thanks_title: "ਧੰਨਵਾਦ",
    name_ph: "ਤੁਹਾਡਾ ਨਾਮ…",
    continue: "ਅੱਗੇ",
    step_of: "ਕਦਮ",
    of: "ਵਿੱਚੋਂ",
    talk_to_person: "ਵੇਟਲਿਸਟ ਵਿੱਚ ਜੁੜੋ",
    back_chat: "ਚੈਟ ਤੇ ਵਾਪਸ",
    nudge_title: "ਇਨਸਾਨੀ ਮਦਦ ਚਾਹੀਦੀ?",
    nudge_body: "ਹੁਣੇ ਸਿਰਫ਼ Saathi (AI) ਹੈ। ਅਸੀਂ ਅਸਲੀ ਇਨਸਾਨ ਵਾਲੀ ਸੇਵਾ ਬਣਾ ਰਹੇ ਹਾਂ — ਵੇਟਲਿਸਟ ਵਿੱਚ ਜੁੜੋ, ਤਿਆਰ ਹੁੰਦੇ ਹੀ ਸੰਪਰਕ ਕਰਾਂਗੇ।",
    nudge_cta: "ਵੇਟਲਿਸਟ ਵਿੱਚ ਜੋੜੋ",
    morning: "🌅 ਸਵੇਰੇ (9am–12pm)",
    afternoon: "☀️ ਦੁਪਹਿਰੇ (12–5pm)",
    evening: "🌙 ਸ਼ਾਮ (5–9pm)",
  },
  ur: {
    greeting: "السلام علیکم! میں ساتھی ہوں — کینیڈا میں آپ کا دوست۔",
    intro: "صحت، سرکاری خطوط، کرایہ داروں کے حقوق، ٹیکس، اسکول — جو بھی ہو، بتائیں۔ میں سننے کے لیے یہاں ہوں۔",
    placeholder: "بتائیں، ذہن میں کیا ہے…",
    name_q: "آپ کا نام کیا ہے؟",
    wa_q: "اپنا WhatsApp نمبر دیں — جب ہماری انسانی ٹیم تیار ہو گی تو رابطہ کریں گے۔",
    time_q: "آپ سے بات کرنے کا بہترین وقت کیا ہے؟",
    thanks_title: "شکریہ",
    name_ph: "آپ کا نام…",
    continue: "آگے",
    step_of: "مرحلہ",
    of: "میں سے",
    talk_to_person: "ویٹ لسٹ میں شامل ہوں",
    back_chat: "چیٹ پر واپس",
    nudge_title: "انسانی مدد چاہیے؟",
    nudge_body: "ابھی صرف Saathi (AI) ہے۔ ہم ایک اصلی انسان والی سروس بنا رہے ہیں — ویٹ لسٹ میں شامل ہوں، تیار ہوتے ہی رابطہ کریں گے۔",
    nudge_cta: "ویٹ لسٹ میں شامل کریں",
    morning: "🌅 صبح (9am–12pm)",
    afternoon: "☀️ دوپہر (12–5pm)",
    evening: "🌙 شام (5–9pm)",
  },
  ta: {
    greeting: "வணக்கம்! நான் சாத்தி — கனடாவில் உங்கள் நண்பன்.",
    intro: "ஆரோக்கியம், அரசு கடிதங்கள், குடியிருப்பாளர் உரிமைகள், வரி, பள்ளி — எதுவானாலும் சொல்லுங்கள். நான் கேட்க இங்கே இருக்கிறேன்.",
    placeholder: "சொல்லுங்கள், மனதில் என்ன இருக்கிறது…",
    name_q: "உங்கள் பெயர் என்ன?",
    wa_q: "உங்கள் WhatsApp எண்ணைக் கொடுங்கள் — எங்கள் மனித குழு தயாரானதும் தொடர்பு கொள்வோம்.",
    time_q: "உங்களைத் தொடர்புகொள்ள சிறந்த நேரம் எது?",
    thanks_title: "நன்றி",
    name_ph: "உங்கள் பெயர்…",
    continue: "தொடரவும்",
    step_of: "படி",
    of: "இல்",
    talk_to_person: "வெயிட்லிஸ்டில் சேரு",
    back_chat: "அரட்டைக்கு திரும்பு",
    nudge_title: "மனித உதவி வேண்டுமா?",
    nudge_body: "இப்போது Saathi (AI) மட்டுமே உள்ளது. நாங்கள் ஒரு உண்மையான மனித சேவையை உருவாக்குகிறோம் — வெயிட்லிஸ்டில் சேருங்கள், தயாரானதும் தொடர்பு கொள்வோம்.",
    nudge_cta: "வெயிட்லிஸ்டில் சேர்",
    morning: "🌅 காலை (9am–12pm)",
    afternoon: "☀️ மதியம் (12–5pm)",
    evening: "🌙 மாலை (5–9pm)",
  },
  bn: {
    greeting: "নমস্কার! আমি সাথী — কানাডায় আপনার বন্ধু।",
    intro: "স্বাস্থ্য, সরকারি চিঠি, ভাড়াটিয়ার অধিকার, কর, স্কুল — যা-ই হোক, বলুন। আমি শুনতে এখানে আছি।",
    placeholder: "বলুন, মনে কী আছে…",
    name_q: "আপনার নাম কী?",
    wa_q: "আপনার WhatsApp নম্বর দিন — আমাদের মানব দল প্রস্তুত হলে আমরা যোগাযোগ করব।",
    time_q: "আপনার সাথে কথা বলার সেরা সময় কখন?",
    thanks_title: "ধন্যবাদ",
    name_ph: "আপনার নাম…",
    continue: "এগিয়ে যান",
    step_of: "ধাপ",
    of: "এর মধ্যে",
    talk_to_person: "ওয়েটলিস্টে যোগ দিন",
    back_chat: "চ্যাটে ফিরে যান",
    nudge_title: "মানুষের সাহায্য চাই?",
    nudge_body: "এখন শুধু Saathi (AI) আছে। আমরা একজন আসল মানুষের সেবা তৈরি করছি — ওয়েটলিস্টে যোগ দিন, প্রস্তুত হলে যোগাযোগ করব।",
    nudge_cta: "ওয়েটলিস্টে যোগ করুন",
    morning: "🌅 সকাল (9am–12pm)",
    afternoon: "☀️ দুপুর (12–5pm)",
    evening: "🌙 সন্ধ্যা (5–9pm)",
  },
  gu: {
    greeting: "નમસ્તે! હું સાથી છું — કૅનેડામાં તમારો મિત્ર.",
    intro: "આરોગ્ય, સરકારી પત્રો, ભાડૂત અધિકારો, ટેક્સ, શાળા — જે પણ હોય, કહો. હું સાંભળવા માટે અહીં છું.",
    placeholder: "કહો, મનમાં શું છે…",
    name_q: "તમારું નામ શું છે?",
    wa_q: "તમારો WhatsApp નંબર આપો — અમારી માનવ ટીમ તૈયાર થશે ત્યારે અમે સંપર્ક કરીશું.",
    time_q: "તમારી સાથે વાત કરવાનો શ્રેષ્ઠ સમય કયો છે?",
    thanks_title: "આભાર",
    name_ph: "તમારું નામ…",
    continue: "આગળ",
    step_of: "પગલું",
    of: "માંથી",
    talk_to_person: "વેઇટલિસ્ટમાં જોડાઓ",
    back_chat: "ચેટ પર પાછા",
    nudge_title: "માનવ સહાય જોઈએ છે?",
    nudge_body: "હમણાં ફક્ત Saathi (AI) છે. અમે એક વાસ્તવિક માનવ સેવા બનાવી રહ્યા છીએ — વેઇટલિસ્ટમાં જોડાઓ, તૈયાર થતાં જ સંપર્ક કરીશું.",
    nudge_cta: "વેઇટલિસ્ટમાં ઉમેરો",
    morning: "🌅 સવાર (9am–12pm)",
    afternoon: "☀️ બપોર (12–5pm)",
    evening: "🌙 સાંજ (5–9pm)",
  },
  tl: {
    greeting: "Kumusta! Ako si Saathi — kaibigan mo sa Canada.",
    intro: "Kalusugan, mga sulat ng gobyerno, karapatan bilang umuupa, buwis, paaralan — kahit ano pa, sabihin mo. Narito ako para makinig.",
    placeholder: "Sabihin mo, ano ang nasa isip mo…",
    name_q: "Ano ang pangalan mo?",
    wa_q: "Ano ang WhatsApp number mo — kapag handa na ang aming human team, kami ang lalapit.",
    time_q: "Anong oras ang pinakamaganda para makausap ka?",
    thanks_title: "Salamat",
    name_ph: "Pangalan mo…",
    continue: "Magpatuloy",
    step_of: "Hakbang",
    of: "sa",
    talk_to_person: "Sumali sa waitlist",
    back_chat: "Bumalik sa chat",
    nudge_title: "Kailangan ng tulong ng tao?",
    nudge_body: "Ngayon, si Saathi (AI) lang ang available. Gumagawa kami ng totoong serbisyo na may tao — sumali sa waitlist, kami ang lalapit pagkahanda.",
    nudge_cta: "Idagdag sa waitlist",
    morning: "🌅 Umaga (9am–12pm)",
    afternoon: "☀️ Hapon (12–5pm)",
    evening: "🌙 Gabi (5–9pm)",
  },
  ar: {
    greeting: "مرحباً! أنا ساثي — صديقك في كندا.",
    intro: "الصحة، الرسائل الحكومية، حقوق المستأجر، الضرائب، المدرسة — أياً كان، أخبرني. أنا هنا لأستمع.",
    placeholder: "أخبرني، ما الذي يدور في ذهنك…",
    name_q: "ما اسمك؟",
    wa_q: "ما رقم WhatsApp الخاص بك — عندما يكون فريقنا البشري جاهزاً سنتواصل معك.",
    time_q: "ما أفضل وقت للتواصل معك؟",
    thanks_title: "شكراً",
    name_ph: "اسمك…",
    continue: "متابعة",
    step_of: "خطوة",
    of: "من",
    talk_to_person: "انضم لقائمة الانتظار",
    back_chat: "العودة إلى الدردشة",
    nudge_title: "تحتاج مساعدة بشرية؟",
    nudge_body: "حالياً فقط Saathi (AI) متاح. نحن نبني خدمة بشرية حقيقية — انضم لقائمة الانتظار وسنتواصل معك عند الجاهزية.",
    nudge_cta: "أضفني للقائمة",
    morning: "🌅 صباحاً (9am–12pm)",
    afternoon: "☀️ ظهراً (12–5pm)",
    evening: "🌙 مساءً (5–9pm)",
  },
  en: {
    greeting: "Hello! I'm Saathi — your friend in Canada.",
    intro: "Healthcare, government letters, tenant rights, taxes, schools — whatever's on your mind, tell me. I'm here to listen.",
    placeholder: "What's on your mind…",
    name_q: "What's your name?",
    wa_q: "What's your WhatsApp number — we'll reach out when our human support is ready.",
    time_q: "What's the best time to reach you?",
    thanks_title: "Thank you",
    name_ph: "Your name…",
    continue: "Continue",
    step_of: "Step",
    of: "of",
    talk_to_person: "Join waitlist",
    back_chat: "Back to chat",
    nudge_title: "Need human help?",
    nudge_body: "Right now it's just Saathi (AI). We're building a real human follow-up service — join the waitlist and we'll reach out as soon as it's ready.",
    nudge_cta: "Add me to waitlist",
    morning: "🌅 Morning (9am–12pm)",
    afternoon: "☀️ Afternoon (12–5pm)",
    evening: "🌙 Evening (5–9pm)",
  },
};

const getCopy = (code) => COPY[code] || COPY.en;

// Suggestion chips per language — warm, specific starters
const SUGGESTIONS = {
  hi: ["OHIP कार्ड कैसे लूँ?", "CRA से चिट्ठी आई है", "मकान मालिक किराया बढ़ा रहे हैं", "T4 क्या होता है?"],
  pa: ["OHIP ਕਾਰਡ ਕਿਵੇਂ ਲਵਾਂ?", "CRA ਤੋਂ ਚਿੱਠੀ ਆਈ", "ਮਕਾਨ ਮਾਲਕ ਕਿਰਾਇਆ ਵਧਾ ਰਿਹਾ", "T4 ਕੀ ਹੁੰਦਾ?"],
  ur: ["OHIP کارڈ کیسے لوں؟", "CRA سے خط آیا", "مالک مکان کرایہ بڑھا رہا", "T4 کیا ہوتا ہے؟"],
  ta: ["OHIP கார்டு எப்படி?", "CRA-லிருந்து கடிதம்", "வீட்டு உரிமையாளர் வாடகை அதிகரிப்பு", "T4 என்றால் என்ன?"],
  bn: ["OHIP কার্ড কীভাবে?", "CRA থেকে চিঠি এসেছে", "বাড়িওয়ালা ভাড়া বাড়াচ্ছে", "T4 মানে কী?"],
  gu: ["OHIP કાર્ડ કેવી રીતે?", "CRA નો પત્ર આવ્યો", "મકાનમાલિક ભાડું વધારે", "T4 શું છે?"],
  tl: ["Paano kumuha ng OHIP?", "May sulat galing CRA", "Tumataas ang upa", "Ano ang T4?"],
  ar: ["كيف أحصل على OHIP؟", "وصلت رسالة من CRA", "المالك يرفع الإيجار", "ما هو T4؟"],
  en: ["How do I get an OHIP card?", "I got a CRA letter", "My landlord is raising rent", "What is a T4?"],
};

// Labels for Document Reader / Share / Support (multilingual, short)
const X = {
  hi: { doc: "📄 दस्तावेज़ समझाइए", doc_cta: "चिट्ठी अपलोड करें", doc_hint: "CRA, T4, किरायेदारी — कोई भी कनाडा की चिट्ठी की फोटो खींचिए या PDF अपलोड कीजिए।", share: "दोस्तों को बताइए", share_msg: "नमस्ते! मैंने Saathi app खोजा है — यह कनाडा में रहने वाले हम भारतीयों की अपनी भाषा में मदद करता है। OHIP कार्ड, CRA चिट्ठी, अधिकार, टैक्स — सब कुछ। मुफ्त है, कोई साइन-अप नहीं। देखिए:", support: "Saathi को सहारा दीजिए", support_body: "अगर आपको Saathi से मदद मिली, आप हमें एक कप चाय मँगवा सकते हैं — हमारी सेवा को चलाने में मदद करता है।", explain_now: "समझाइए" },
  pa: { doc: "📄 ਚਿੱਠੀ ਸਮਝਾਓ", doc_cta: "ਚਿੱਠੀ ਅਪਲੋਡ ਕਰੋ", doc_hint: "CRA, T4, ਕਿਰਾਏਦਾਰੀ — ਕੈਨੇਡਾ ਦੀ ਕੋਈ ਵੀ ਚਿੱਠੀ ਦੀ ਫੋਟੋ ਜਾਂ PDF ਅੱਪਲੋਡ ਕਰੋ।", share: "ਦੋਸਤਾਂ ਨੂੰ ਦੱਸੋ", share_msg: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ! ਮੈਂ Saathi ਲੱਭਿਆ — ਕੈਨੇਡਾ ਵਿੱਚ ਪੰਜਾਬੀ ਵਿੱਚ ਮਦਦ। OHIP, CRA, ਟੈਕਸ, ਹੱਕ। ਮੁਫ਼ਤ। ਵੇਖੋ:", support: "Saathi ਦਾ ਸਾਥ ਦਿਓ", support_body: "ਜੇ Saathi ਨੇ ਮਦਦ ਕੀਤੀ, ਤੁਸੀਂ ਇੱਕ ਕੱਪ ਚਾਹ ਭੇਜ ਸਕਦੇ ਹੋ।", explain_now: "ਸਮਝਾਓ" },
  ur: { doc: "📄 خط سمجھائیں", doc_cta: "خط اپ لوڈ کریں", doc_hint: "CRA, T4 — کسی بھی کینیڈین خط کی تصویر یا PDF اپ لوڈ کریں۔", share: "دوستوں کو بتائیں", share_msg: "السلام علیکم! میں نے Saathi دیکھا — اردو میں کینیڈا کی مدد۔ مفت۔ دیکھیں:", support: "Saathi کی حمایت کریں", support_body: "اگر Saathi نے مدد کی، آپ ایک کپ چائے بھیج سکتے ہیں۔", explain_now: "سمجھائیں" },
  ta: { doc: "📄 ஆவணத்தை விளக்கு", doc_cta: "ஆவணத்தை பதிவேற்று", doc_hint: "CRA, T4 — புகைப்படம் அல்லது PDF.", share: "நண்பர்களுக்கு சொல்லு", share_msg: "வணக்கம்! Saathi — தமிழில் கனடாவில் உதவி. இலவசம். பாருங்கள்:", support: "Saathi-யை ஆதரி", support_body: "Saathi உதவியதெனில் ஒரு கப் தேநீர் அனுப்ப முடியும்.", explain_now: "விளக்கு" },
  bn: { doc: "📄 ডকুমেন্ট বোঝান", doc_cta: "ডকুমেন্ট আপলোড", doc_hint: "CRA, T4 — ছবি বা PDF।", share: "বন্ধুদের বলুন", share_msg: "নমস্কার! Saathi — বাংলায় কানাডায় সাহায্য। বিনামূল্যে। দেখুন:", support: "Saathi-কে সমর্থন", support_body: "Saathi সাহায্য করলে এক কাপ চা পাঠাতে পারেন।", explain_now: "বোঝান" },
  gu: { doc: "📄 દસ્તાવેજ સમજાવો", doc_cta: "દસ્તાવેજ અપલોડ", doc_hint: "CRA, T4 — ફોટો કે PDF.", share: "મિત્રોને કહો", share_msg: "નમસ્તે! Saathi — ગુજરાતીમાં કેનેડામાં મદદ. મફત.", support: "Saathi ને ટેકો", support_body: "મદદ ગમી તો એક કપ ચા મોકલી શકો.", explain_now: "સમજાવો" },
  tl: { doc: "📄 Ipaliwanag ang dokumento", doc_cta: "Mag-upload", doc_hint: "CRA, T4 — larawan o PDF.", share: "Ibahagi sa kaibigan", share_msg: "Kumusta! May nakita akong Saathi — tulong sa Canada sa Tagalog. Libre. Tingnan:", support: "Suportahan ang Saathi", support_body: "Kung nakatulong, pwede kayong mag-abot ng isang kape.", explain_now: "Ipaliwanag" },
  ar: { doc: "📄 اشرح المستند", doc_cta: "ارفع المستند", doc_hint: "CRA, T4 — صورة أو PDF.", share: "أخبر أصدقاءك", share_msg: "مرحبا! اكتشفت Saathi — مساعدة بالعربية في كندا. مجاناً. شاهد:", support: "ادعم Saathi", support_body: "إذا ساعدك Saathi، يمكنك إرسال فنجان قهوة.", explain_now: "اشرح" },
  en: { doc: "📄 Explain a document", doc_cta: "Upload a document", doc_hint: "CRA letter, T4, lease, benefit letter — take a photo or upload a PDF and I'll explain it in plain language.", share: "Share with a friend", share_msg: "Hi! I found Saathi — an app that helps newcomers in Canada in their own language. Free, no sign-up. Check it out:", support: "Support Saathi", support_body: "If Saathi has helped you, you can send us a coffee — it keeps this service free for everyone.", explain_now: "Explain this" },
};
const getX = (code) => X[code] || X.en;

// ⚠️ Swap these two URLs for your real ones when ready.
const SUPPORT_URL = "https://buymeacoffee.com/hikarigrove";
const SAATHI_SHARE_URL = typeof window !== "undefined" ? window.location.origin : "https://saathi.hikarigrove.com";

export default function HikariSaathi({ orgSlug = null }) {
  const orgName = useOrgName(orgSlug);
  const [screen, setScreen] = useState("welcome"); // welcome | language | chat
  const [selectedLang, setSelectedLang] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showNudge, setShowNudge] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupStep, setFollowupStep] = useState(0); // 0 name, 1 wa, 2 time, 3 done
  const [followup, setFollowup] = useState({ name: "", whatsapp: "", time: "" });
  const [tempVal, setTempVal] = useState("");

  // ── Voice state ──
  const [voiceMode, setVoiceMode] = useState(() => localStorage.getItem("saathi_voice") === "1");
  const [recState, setRecState] = useState("idle"); // idle | recording | transcribing
  const [playingIdx, setPlayingIdx] = useState(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const audioElRef = useRef(null);

  // ── Document reader / modals ──
  const [docOpen, setDocOpen] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [docNote, setDocNote] = useState("");
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const c = selectedLang ? getCopy(selectedLang.code) : getCopy("en");
  const isRTL = selectedLang?.rtl || false;
  const dir = isRTL ? "rtl" : "ltr";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, showNudge]);

  const selectLanguage = (lang) => {
    setSelectedLang(lang);
    const copy = getCopy(lang.code);
    setMessages([{ role: "assistant", content: `${copy.greeting}\n\n${copy.intro}` }]);
    setScreen("call");
  };

  const finishCall = ({ turns, conversationId: cid }) => {
    setScreen("chat");
    if (turns.length) setMessages((prev) => [...prev, ...turns]);
    if (cid && !conversationId) setConversationId(cid);
  };

  const sendMessage = async (textOverride) => {
    const userText = (textOverride ?? input).trim();
    if (!userText || loading) return;
    setInput("");
    const next = [...messages, { role: "user", content: userText }];
    setMessages(next);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        language_code: selectedLang.code,
        language_name: selectedLang.sub,
        messages: next,
        conversation_id: conversationId,
        org_slug: orgSlug,
        client_id: getClientId(),
      });
      const { reply, conversation_id } = res.data;
      if (!conversationId) setConversationId(conversation_id);
      const replyIdx = next.length; // index the reply will sit at
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);

      // Auto-play the reply if voice mode is on
      if (voiceMode) {
        setTimeout(() => playMessage(replyIdx, reply), 250);
      }

      // Show follow-up nudge after user has sent 2+ messages
      const userMsgs = next.filter((m) => m.role === "user").length;
      if (userMsgs >= 2 && !showNudge && !followupOpen) {
        setTimeout(() => setShowNudge(true), 900);
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: isDailyLimit(e) ? DAILY_LIMIT_MSG : "I'm having trouble connecting right now. Please try again in a moment." },
      ]);
    }
    setLoading(false);
  };

  // ── Voice: record & transcribe ──
  const startRecording = async () => {
    if (recState !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 400) { setRecState("idle"); return; }
        setRecState("transcribing");
        try {
          const fd = new FormData();
          const ext = (mr.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
          fd.append("audio", blob, `rec.${ext}`);
          fd.append("language_code", selectedLang.code);
          const res = await axios.post(`${API}/voice/transcribe`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (res.data?.text || "").trim();
          if (text) {
            setRecState("idle");
            await sendMessage(text);
          } else {
            setRecState("idle");
          }
        } catch {
          setRecState("idle");
        }
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecState("recording");
    } catch {
      alert("Could not access microphone. Please allow mic permission.");
      setRecState("idle");
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && recState === "recording") {
      mediaRecRef.current.stop();
    }
  };

  const micTap = () => {
    if (recState === "recording") stopRecording();
    else if (recState === "idle") startRecording();
  };

  // ── Voice: play a message via TTS ──
  const stopPlayback = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.src = "";
      audioElRef.current = null;
    }
    setPlayingIdx(null);
  };

  const playMessage = async (idx, text) => {
    stopPlayback();
    setPlayingIdx(idx);
    try {
      const res = await axios.post(
        `${API}/voice/tts`,
        { text, language_code: selectedLang.code, voice: "coral" },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      audioElRef.current = a;
      a.onended = () => { setPlayingIdx(null); URL.revokeObjectURL(url); audioElRef.current = null; };
      a.onerror = () => { setPlayingIdx(null); URL.revokeObjectURL(url); audioElRef.current = null; };
      await a.play();
    } catch {
      setPlayingIdx(null);
    }
  };

  const toggleVoiceMode = () => {
    const next = !voiceMode;
    setVoiceMode(next);
    localStorage.setItem("saathi_voice", next ? "1" : "0");
    if (!next) stopPlayback();
  };

  useEffect(() => () => stopPlayback(), []);

  // ── Document reader ──
  const onPickDoc = (f) => {
    setDocError("");
    if (!f) return;
    const ok = ["image/jpeg","image/jpg","image/png","image/webp","image/heic","application/pdf"];
    if (!ok.includes(f.type)) {
      setDocError("Please pick a JPG, PNG, WEBP, HEIC, or PDF file.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setDocError("File too large (max 10 MB).");
      return;
    }
    setDocFile(f);
    if (f.type.startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setDocPreview({ type: "image", url, name: f.name });
    } else {
      setDocPreview({ type: "pdf", name: f.name, size: f.size });
    }
  };

  const submitDoc = async () => {
    if (!docFile || docLoading || !selectedLang) return;
    setDocLoading(true);
    setDocError("");
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("language_code", selectedLang.code);
      fd.append("language_name", selectedLang.sub);
      if (docNote.trim()) fd.append("note", docNote.trim());
      if (orgSlug) fd.append("org_slug", orgSlug);
      fd.append("client_id", getClientId());
      const res = await axios.post(`${API}/documents/explain`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const explanation = res.data?.explanation || "";
      // Push explanation into chat as an assistant message + a user context line
      const userLine = `[${getX(selectedLang.code).doc}: ${docFile.name}]${docNote.trim() ? " — " + docNote.trim() : ""}`;
      setMessages((prev) => [
        ...prev,
        { role: "user", content: userLine },
        { role: "assistant", content: explanation },
      ]);
      // Reset + close
      if (docPreview?.url) URL.revokeObjectURL(docPreview.url);
      setDocFile(null);
      setDocPreview(null);
      setDocNote("");
      setDocOpen(false);
    } catch (e) {
      setDocError(isDailyLimit(e) ? DAILY_LIMIT_MSG : (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please try again."));
    }
    setDocLoading(false);
  };

  const closeDoc = () => {
    if (docPreview?.url) URL.revokeObjectURL(docPreview.url);
    setDocFile(null);
    setDocPreview(null);
    setDocNote("");
    setDocError("");
    setDocOpen(false);
  };

  // ── Share Saathi (native share → WhatsApp fallback) ──
  const doShare = async () => {
    const copy = getX(selectedLang?.code || "en");
    const text = `${copy.share_msg} ${SAATHI_SHARE_URL}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Saathi", text, url: SAATHI_SHARE_URL });
        setShareOpen(false);
        return;
      }
    } catch { /* fall through */ }
    // WhatsApp web fallback
    const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(waUrl, "_blank", "noopener");
    setShareOpen(false);
  };

  const copyShare = async () => {
    const copy = getX(selectedLang?.code || "en");
    const text = `${copy.share_msg} ${SAATHI_SHARE_URL}`;
    try { await navigator.clipboard.writeText(text); } catch { /* silent */ }
  };

  const openFollowup = () => {
    setFollowupOpen(true);
    setFollowupStep(0);
    setTempVal("");
    setShowNudge(false);
  };

  const submitFollowupStep = async (value) => {
    if (followupStep === 0) {
      setFollowup((f) => ({ ...f, name: value }));
      setFollowupStep(1);
      setTempVal("");
    } else if (followupStep === 1) {
      setFollowup((f) => ({ ...f, whatsapp: value }));
      setFollowupStep(2);
      setTempVal("");
    } else if (followupStep === 2) {
      const fullData = { ...followup, time: value };
      setFollowup(fullData);
      try {
        await axios.post(`${API}/followups`, {
          name: fullData.name,
          whatsapp: fullData.whatsapp,
          time: fullData.time,
          language_code: selectedLang.code,
          language_name: selectedLang.sub,
          conversation_id: conversationId,
        });
      } catch (e) {
        // silent — still show thanks
      }
      setFollowupStep(3);
    }
  };

  // ═══════════════════════════════════ WELCOME ═══════════════════════════════════
  const modals = (
    <>
      {/* Document Reader modal */}
      {docOpen && selectedLang && (
        <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center" data-testid="doc-modal">
          <div className="absolute inset-0 bg-[#1A1A2E]/50 backdrop-blur-sm" onClick={closeDoc} />
          <div className="relative w-full sm:max-w-md bg-[#FFFBF5] sm:rounded-3xl rounded-t-3xl border border-[#EDE0D4] shadow-2xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E07B39] mb-1">saathi · document reader</div>
                <h3 className="font-heading text-[28px] leading-[1.05] text-[#1A1A2E]">{getX(selectedLang.code).doc}</h3>
              </div>
              <button onClick={closeDoc} className="w-9 h-9 rounded-full bg-white border border-[#EDE0D4] text-[#8B7355] hover:text-[#1A1A2E] hover:border-[#E07B39]" data-testid="doc-close-btn">×</button>
            </div>
            <p className="text-[14px] text-[#8B7355] leading-relaxed mb-5">{getX(selectedLang.code).doc_hint}</p>

            {!docPreview ? (
              <label className="block border-2 border-dashed border-[#EDE0D4] hover:border-[#E07B39] rounded-3xl p-8 text-center cursor-pointer bg-white transition-colors">
                <div className="text-4xl mb-3">📄</div>
                <div className="font-heading text-[18px] text-[#1A1A2E] mb-1">{getX(selectedLang.code).doc_cta}</div>
                <div className="font-mono text-[10px] text-[#8B7355] tracking-wider">JPG · PNG · WEBP · HEIC · PDF · max 10 MB</div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                  onChange={(e) => onPickDoc(e.target.files?.[0])}
                  className="hidden"
                  data-testid="doc-file-input"
                />
              </label>
            ) : (
              <div className="bg-white border border-[#EDE0D4] rounded-2xl p-4 mb-4">
                {docPreview.type === "image" ? (
                  <img src={docPreview.url} alt="preview" className="w-full max-h-64 object-contain rounded-xl mb-3" />
                ) : (
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-14 h-14 rounded-xl bg-[#FFF3E0] border border-[#EDE0D4] flex items-center justify-center text-2xl">📄</div>
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium text-[#1A1A2E] truncate">{docPreview.name}</div>
                      <div className="font-mono text-[11px] text-[#8B7355]">PDF · {(docPreview.size / 1024).toFixed(0)} KB</div>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => { if (docPreview?.url) URL.revokeObjectURL(docPreview.url); setDocFile(null); setDocPreview(null); }}
                  className="font-mono text-[11px] text-[#8B7355] hover:text-[#1A1A2E]"
                  data-testid="doc-change-btn"
                >
                  change file
                </button>
              </div>
            )}

            {docPreview && (
              <textarea
                value={docNote}
                onChange={(e) => setDocNote(e.target.value)}
                placeholder="Optional: tell me what's confusing about this letter…"
                rows={2}
                className="w-full bg-white border border-[#EDE0D4] rounded-2xl px-4 py-3 text-[14px] text-[#1A1A2E] placeholder:text-[#8B7355]/70 focus:outline-none focus:border-[#E07B39] focus:ring-2 focus:ring-[#E07B39]/20 resize-none font-body mb-4"
                data-testid="doc-note-input"
                dir={isRTL ? "rtl" : "ltr"}
              />
            )}

            {docError && <div className="text-[13px] text-[#D32F2F] mb-3" data-testid="doc-error">{docError}</div>}

            <button
              disabled={!docFile || docLoading}
              onClick={submitDoc}
              className={`w-full rounded-full py-3.5 text-[15px] font-medium min-h-[52px] transition-all ${
                docFile && !docLoading
                  ? "bg-[#E07B39] hover:bg-[#c96a2e] text-white active:scale-[0.98] shadow-[0_4px_16px_-4px_rgba(224,123,57,0.5)]"
                  : "bg-[#EDE0D4] text-[#8B7355] cursor-not-allowed"
              }`}
              data-testid="doc-submit-btn"
            >
              {docLoading ? "reading…" : `${getX(selectedLang.code).explain_now} →`}
            </button>
          </div>
        </div>
      )}

      {/* Share modal */}
      {shareOpen && selectedLang && (
        <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center" data-testid="share-modal">
          <div className="absolute inset-0 bg-[#1A1A2E]/50 backdrop-blur-sm" onClick={() => setShareOpen(false)} />
          <div className="relative w-full sm:max-w-md bg-[#FFFBF5] sm:rounded-3xl rounded-t-3xl border border-[#EDE0D4] shadow-2xl p-6 sm:p-8">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E07B39] mb-1">saathi</div>
                <h3 className="font-heading text-[28px] leading-[1.05] text-[#1A1A2E]">{getX(selectedLang.code).share}</h3>
              </div>
              <button onClick={() => setShareOpen(false)} className="w-9 h-9 rounded-full bg-white border border-[#EDE0D4] text-[#8B7355] hover:text-[#1A1A2E] hover:border-[#E07B39]">×</button>
            </div>
            <p className="text-[14px] text-[#8B7355] leading-relaxed mb-4">
              Saathi works because people share it with someone who needs it. One tap sends a warm message on WhatsApp in your language.
            </p>
            <div className="bg-white border border-[#EDE0D4] rounded-2xl px-4 py-3 text-[14px] text-[#1A1A2E] leading-relaxed mb-5 font-body" dir={isRTL ? "rtl" : "ltr"}>
              {getX(selectedLang.code).share_msg} <span className="text-[#E07B39]">{SAATHI_SHARE_URL}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={doShare}
                className="flex-1 bg-[#25D366] hover:bg-[#1ea952] text-white rounded-full py-3.5 text-[15px] font-medium min-h-[52px] flex items-center justify-center gap-2 active:scale-[0.98]"
                data-testid="share-whatsapp-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4l-2.7-1.3c-.3-.2-.7-.1-.9.2l-.8 1c-1.5-.8-2.7-2-3.5-3.5l1-.8c.3-.2.4-.6.2-.9L9.6 6.5c-.2-.4-.6-.5-1-.3l-2 1c-.4.2-.6.6-.5 1 .7 4.7 4.4 8.4 9.1 9.1.4.1.8-.1 1-.5l1-2c.2-.4.1-.8-.3-1z"/></svg>
                WhatsApp
              </button>
              <button
                onClick={copyShare}
                className="bg-white border border-[#EDE0D4] text-[#1A1A2E] rounded-full px-5 text-[13px] font-medium min-h-[52px] hover:border-[#E07B39]"
                data-testid="share-copy-btn"
              >
                copy
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );

  // ═══════════════════════════════════ WELCOME ═══════════════════════════════════
  if (screen === "welcome") {
    return (
      <>
      <div className="saathi-page" data-testid="welcome-screen">
        <div className="saathi-texture" />
        <div className="flex min-h-screen flex-col items-start justify-between px-7 sm:px-10 py-12 relative z-10 max-w-md mx-auto w-full">
          <div className="w-full">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#8B7355]" data-testid="hikari-eyebrow">
              Hikari Labs
            </div>
            {orgName && (
              <div className="mt-3 inline-flex items-center gap-2 bg-white border border-[#EDE0D4] rounded-full px-3 py-1.5 text-[12px] text-[#1A1A2E]" data-testid="org-provided-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E07B39]" />
                Provided by <strong>{orgName}</strong>
              </div>
            )}
          </div>

          <div className="w-full mt-20 mb-16 welcome-stagger">
            <div className="text-5xl mb-8 stag-1">🌸</div>
            <h1
              className="font-heading text-[64px] leading-[0.95] tracking-tight text-[#1A1A2E] mb-4 stag-2"
              data-testid="welcome-title"
            >
              Saathi.
            </h1>
            <p className="font-heading italic text-[22px] leading-snug text-[#8B7355] mb-8 stag-3">
              साथी · ਸਾਥੀ · دوست · 纽带
            </p>
            <p className="text-[17px] leading-[1.65] text-[#1A1A2E]/80 max-w-[340px] stag-4">
              Your friend in Canada. Here when you need to understand a letter, ask about your rights, or just talk things through.
            </p>
          </div>

          <div className="w-full stag-5">
            <button
              className="w-full bg-[#E07B39] hover:bg-[#c96a2e] text-white font-medium text-[17px] rounded-full px-8 py-5 min-h-[56px] transition-all active:scale-[0.98] shadow-[0_8px_24px_-8px_rgba(224,123,57,0.5)]"
              onClick={() => setScreen("language")}
              data-testid="get-started-btn"
            >
              Get started →
            </button>
            <div className="flex items-center justify-center gap-4 mt-6 font-mono text-[10px] tracking-wider text-[#8B7355]">
              <a href="/privacy" className="hover:text-[#E07B39]" data-testid="welcome-privacy-link">Privacy</a>
              <span className="text-[#EDE0D4]">·</span>
              <a href="/terms" className="hover:text-[#E07B39]" data-testid="welcome-terms-link">Terms</a>
              {!orgSlug && <span className="text-[#EDE0D4]">·</span>}
              {!orgSlug && <a href="/org/login" className="hover:text-[#E07B39]" data-testid="welcome-partner-login-link">Partner sign in</a>}
            </div>
          </div>
        </div>
      </div>
      {modals}
      </>
    );
  }

  // ═══════════════════════════════════ LANGUAGE ═══════════════════════════════════
  if (screen === "language") {
    return (
      <div className="saathi-page" data-testid="language-screen">
        <div className="saathi-texture" />
        <div className="max-w-md mx-auto w-full px-7 sm:px-10 py-10 relative z-10">
          <button
            className="font-mono text-xs tracking-wider text-[#8B7355] hover:text-[#1A1A2E] mb-12 transition-colors"
            onClick={() => setScreen("welcome")}
            data-testid="back-to-welcome-btn"
          >
            ← back
          </button>

          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#E07B39] mb-3">
            Step 1 of 1
          </div>
          <h2 className="font-heading text-[42px] leading-[1] tracking-tight text-[#1A1A2E] mb-3">
            Choose your<br />language.
          </h2>
          <p className="text-[15px] text-[#8B7355] leading-relaxed mb-10 italic font-heading">
            ਆਪਣੀ ਭਾਸ਼ਾ ਚੁਣੋ · अपनी भाषा चुनिए · اپنی زبان منتخب کریں
          </p>

          <div className="flex flex-col gap-3 lang-stagger">
            {LANGUAGES.map((lang, i) => (
              <button
                key={lang.code}
                className="group flex items-center gap-4 bg-white border border-[#EDE0D4] rounded-2xl px-5 py-4 min-h-[72px] text-start hover:border-[#E07B39] hover:bg-[#FFF3E0]/40 transition-all active:scale-[0.99] shadow-[0_2px_10px_rgba(26,26,46,0.03)]"
                onClick={() => selectLanguage(lang)}
                data-testid={`language-${lang.code}-btn`}
                style={{ animationDelay: `${i * 40}ms` }}
                dir={lang.rtl ? "rtl" : "ltr"}
              >
                <span className="text-[30px]">{lang.flag}</span>
                <div className="flex-1">
                  <div className="font-heading text-[22px] leading-tight text-[#1A1A2E]">{lang.label}</div>
                  <div className="font-mono text-[11px] tracking-wider uppercase text-[#8B7355] mt-0.5">{lang.sub}</div>
                </div>
                <span className="text-[#E07B39] text-xl opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════ CALL ═══════════════════════════════════
  if (screen === "call") {
    return (
      <VoiceCall
        lang={selectedLang} orgSlug={orgSlug} history={messages} conversationId={conversationId}
        greeting={messages.some((m) => m.role === "user") ? null : c.greeting}
        onDone={finishCall}
      />
    );
  }

  // ═══════════════════════════════════ CHAT ═══════════════════════════════════
  return (
    <>
    <div className="saathi-page" dir={dir} data-testid="chat-screen">
      <div className="saathi-texture" />
      <div className="max-w-[640px] mx-auto w-full flex flex-col min-h-screen relative z-10">
        {/* Header */}
        <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#FFFBF5]/85 border-b border-[#EDE0D4] px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            className="text-[#8B7355] hover:text-[#1A1A2E] text-lg p-1 transition-colors"
            onClick={() => { setScreen("language"); setMessages([]); setConversationId(null); setShowNudge(false); setFollowupOpen(false); setFollowupStep(0); }}
            data-testid="back-to-lang-btn"
            aria-label="back"
          >
            {isRTL ? "→" : "←"}
          </button>
          <div className="w-9 h-9 rounded-full bg-[#FFF3E0] border border-[#EDE0D4] flex items-center justify-center text-lg shrink-0">🌸</div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-[19px] leading-tight text-[#1A1A2E]">Saathi</div>
            <div className="font-mono text-[10px] tracking-wider uppercase text-[#E07B39]">
              {selectedLang.sub} · always here
            </div>
          </div>
          <button
            className="bg-[#1A1A2E] hover:bg-[#2a2a42] text-white text-xs font-medium px-4 py-2.5 rounded-full min-h-[40px] transition-colors whitespace-nowrap"
            onClick={openFollowup}
            data-testid="talk-to-person-btn"
          >
            {c.talk_to_person}
          </button>
          <button
            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-[#E07B39] hover:bg-[#c96a2e] text-white shadow-[0_2px_10px_rgba(224,123,57,0.4)] transition-colors"
            onClick={() => setScreen("call")}
            aria-label="call Saathi"
            title="Talk to Saathi by voice"
            data-testid="start-call-btn"
          >
            <Phone size={18} />
          </button>
          <button
            className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-all ${
              voiceMode
                ? "bg-[#E07B39] text-white shadow-[0_2px_10px_rgba(224,123,57,0.4)]"
                : "bg-white text-[#8B7355] border border-[#EDE0D4] hover:border-[#E07B39]"
            }`}
            onClick={toggleVoiceMode}
            aria-label={voiceMode ? "voice mode on" : "voice mode off"}
            title={voiceMode ? "Voice replies ON — tap to mute" : "Voice replies OFF — tap to enable"}
            data-testid="voice-mode-toggle"
          >
            {voiceMode ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
            )}
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 pb-56 sm:pb-36" data-testid="messages-container">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex mb-5 msg-enter ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`msg-${msg.role}-${i}`}
            >
              {msg.role === "assistant" && (
                <div className={`w-8 h-8 rounded-full bg-[#FFF3E0] border border-[#EDE0D4] flex items-center justify-center text-sm me-2 shrink-0 mt-1 ${playingIdx === i ? "saathi-avatar-speaking" : ""}`}>🌸</div>
              )}
              <div
                className={`max-w-[82%] px-4 py-3 text-[15px] leading-[1.65] whitespace-pre-wrap relative group ${
                  msg.role === "user"
                    ? "bg-[#0B7EA3] text-white rounded-2xl rounded-ee-sm"
                    : "bg-white text-[#1A1A2E] border border-[#EDE0D4] rounded-2xl rounded-ss-sm shadow-[0_2px_10px_rgba(26,26,46,0.04)]"
                }`}
              >
                {msg.content}
                {msg.role === "assistant" && (
                  <button
                    onClick={() => playingIdx === i ? stopPlayback() : playMessage(i, msg.content)}
                    className={`absolute -bottom-2 end-3 w-7 h-7 rounded-full bg-white border transition-all flex items-center justify-center ${
                      playingIdx === i
                        ? "border-[#E07B39] text-[#E07B39] shadow-[0_2px_8px_rgba(224,123,57,0.3)]"
                        : "border-[#EDE0D4] text-[#8B7355] hover:border-[#E07B39] hover:text-[#E07B39] opacity-0 group-hover:opacity-100 focus:opacity-100"
                    }`}
                    aria-label={playingIdx === i ? "stop" : "play"}
                    data-testid={`play-msg-${i}`}
                  >
                    {playingIdx === i ? (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                    ) : (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Suggestion chips — show only after the initial greeting, before any user message */}
          {messages.length === 1 && !loading && (
            <>
              <div className="flex flex-wrap gap-2 mb-3 ms-10" data-testid="quick-actions">
                <button
                  onClick={() => setDocOpen(true)}
                  className="bg-[#FFF3E0] border border-[#E07B39]/30 text-[#1A1A2E] text-[13px] px-4 py-2 rounded-full hover:bg-[#E07B39] hover:text-white hover:border-[#E07B39] transition-all active:scale-95 font-medium"
                  data-testid="open-doc-reader-btn"
                >
                  {getX(selectedLang.code).doc}
                </button>
                <button
                  onClick={() => setShareOpen(true)}
                  className="bg-white border border-[#EDE0D4] text-[#1A1A2E] text-[13px] px-4 py-2 rounded-full hover:bg-[#FFF3E0] hover:border-[#E07B39] transition-all active:scale-95"
                  data-testid="open-share-btn"
                >
                  ↗ {getX(selectedLang.code).share}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mb-4 ms-10" data-testid="suggestion-chips">
                {(SUGGESTIONS[selectedLang.code] || SUGGESTIONS.en).map((s, i) => (
                  <button
                    key={i}
                    className="bg-white border border-[#EDE0D4] text-[#1A1A2E] text-[13px] px-4 py-2 rounded-full hover:bg-[#FFF3E0] hover:border-[#E07B39] transition-all active:scale-95"
                    onClick={() => sendMessage(s)}
                    data-testid={`suggestion-chip-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}

          {loading && (
            <div className="flex mb-5" data-testid="typing-indicator">
              <div className="w-8 h-8 rounded-full bg-[#FFF3E0] border border-[#EDE0D4] flex items-center justify-center text-sm me-2 shrink-0 mt-1">🌸</div>
              <div className="bg-white border border-[#EDE0D4] rounded-2xl rounded-ss-sm px-5 py-4 flex gap-1.5 items-center shadow-[0_2px_10px_rgba(26,26,46,0.04)]">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full bg-[#E07B39] saathi-pulse"
                    style={{ animationDelay: `${i * 0.18}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Follow-up nudge */}
          {showNudge && !loading && !followupOpen && (
            <div className="bg-white border border-[#EDE0D4] rounded-3xl p-5 my-5 shadow-[0_8px_30px_rgba(26,26,46,0.06)] nudge-enter" data-testid="followup-nudge">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FFF3E0] border border-[#EDE0D4] flex items-center justify-center text-lg shrink-0">💙</div>
                <div className="flex-1">
                  <div className="font-heading text-[19px] leading-tight text-[#1A1A2E] mb-1">{c.nudge_title}</div>
                  <div className="text-[14px] text-[#8B7355] leading-relaxed mb-3">{c.nudge_body}</div>
                  <div className="flex gap-2">
                    <button
                      className="bg-[#E07B39] hover:bg-[#c96a2e] text-white text-[13px] font-medium px-4 py-2 rounded-full min-h-[40px] transition-colors"
                      onClick={openFollowup}
                      data-testid="nudge-yes-btn"
                    >
                      {c.nudge_cta}
                    </button>
                    <button
                      className="text-[#8B7355] hover:text-[#1A1A2E] text-[13px] px-3 py-2 transition-colors"
                      onClick={() => setShowNudge(false)}
                      data-testid="nudge-dismiss-btn"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Inline follow-up flow */}
          {followupOpen && (
            <div className="bg-white border border-[#EDE0D4] rounded-3xl p-6 my-5 shadow-[0_8px_30px_rgba(26,26,46,0.08)] relative overflow-hidden" data-testid="followup-card">
              {/* Progress bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#EDE0D4]">
                <div
                  className="h-full bg-[#E07B39] transition-all duration-400"
                  style={{ width: `${((followupStep + (followupStep === 3 ? 0 : 0.2)) / 3) * 100}%` }}
                />
              </div>

              {followupStep === 3 ? (
                <div className="text-center py-4">
                  <div className="text-5xl mb-4">💙</div>
                  <h3 className="font-heading text-[26px] text-[#1A1A2E] mb-3" data-testid="thanks-title">{c.thanks_title}</h3>
                  <p className="text-[14px] text-[#8B7355] leading-relaxed max-w-[280px] mx-auto mb-6">
                    {followup.name && <span className="font-medium text-[#1A1A2E]">{followup.name}</span>}
                    {followup.name ? ", " : ""}we'll reach you on WhatsApp at <span className="font-mono text-[#1A1A2E]">{followup.whatsapp}</span> during {followup.time}.
                  </p>
                  <button
                    className="bg-[#1A1A2E] text-white text-[13px] px-5 py-2.5 rounded-full min-h-[40px] hover:bg-[#2a2a42] transition-colors"
                    onClick={() => { setFollowupOpen(false); setFollowupStep(0); }}
                    data-testid="back-to-chat-btn"
                  >
                    {c.back_chat}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E07B39] mb-2">
                    {c.step_of} {followupStep + 1} {c.of} 3
                  </div>
                  <h3 className="font-heading text-[22px] leading-tight text-[#1A1A2E] mb-5" data-testid="followup-question">
                    {followupStep === 0 ? c.name_q : followupStep === 1 ? c.wa_q : c.time_q}
                  </h3>

                  {followupStep === 2 ? (
                    <div className="flex flex-col gap-2">
                      {[c.morning, c.afternoon, c.evening].map((t, i) => (
                        <button
                          key={i}
                          className="bg-[#FFFBF5] border border-[#EDE0D4] text-[#1A1A2E] text-[15px] px-4 py-3.5 rounded-2xl hover:border-[#E07B39] hover:bg-[#FFF3E0] transition-all text-start min-h-[48px]"
                          onClick={() => submitFollowupStep(t)}
                          data-testid={`time-option-${i}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <>
                      <input
                        type={followupStep === 1 ? "tel" : "text"}
                        value={tempVal}
                        onChange={(e) => setTempVal(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && tempVal.trim() && submitFollowupStep(tempVal.trim())}
                        placeholder={followupStep === 0 ? c.name_ph : "+1 (xxx) xxx-xxxx"}
                        className="w-full bg-[#FFFBF5] border border-[#EDE0D4] rounded-2xl px-4 py-3.5 text-[15px] text-[#1A1A2E] placeholder:text-[#8B7355]/70 focus:outline-none focus:border-[#E07B39] focus:ring-2 focus:ring-[#E07B39]/20 min-h-[48px] mb-3 font-body"
                        autoFocus
                        dir={isRTL ? "rtl" : "ltr"}
                        data-testid={`followup-input-${followupStep === 0 ? "name" : "whatsapp"}`}
                      />
                      <button
                        className={`w-full rounded-full py-3.5 text-[15px] font-medium min-h-[48px] transition-all ${
                          tempVal.trim()
                            ? "bg-[#E07B39] hover:bg-[#c96a2e] text-white active:scale-[0.98]"
                            : "bg-[#EDE0D4] text-[#8B7355] cursor-not-allowed"
                        }`}
                        onClick={() => tempVal.trim() && submitFollowupStep(tempVal.trim())}
                        disabled={!tempVal.trim()}
                        data-testid="followup-continue-btn"
                      >
                        {c.continue} →
                      </button>
                    </>
                  )}
                  <button
                    className="block mx-auto mt-3 text-[12px] text-[#8B7355] hover:text-[#1A1A2E] transition-colors"
                    onClick={() => { setFollowupOpen(false); setFollowupStep(0); }}
                    data-testid="followup-cancel-btn"
                  >
                    cancel
                  </button>
                </div>
              )}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div className="fixed bottom-0 inset-x-0 border-t border-[#EDE0D4] backdrop-blur-xl bg-[#FFFBF5]/95" style={{ zIndex: 10000 }}>
          <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-3 pb-[88px] sm:pb-5">
            {recState !== "idle" && (
              <div className="flex items-center justify-center gap-2 mb-2 text-[12px] font-mono text-[#E07B39]" data-testid="rec-status">
                {recState === "recording" ? (
                  <>
                    <span className="inline-block w-2 h-2 rounded-full bg-[#E07B39] saathi-pulse" />
                    listening… tap mic to send
                  </>
                ) : (
                  <>
                    <span className="inline-block w-2 h-2 rounded-full bg-[#8B7355] saathi-pulse" />
                    transcribing…
                  </>
                )}
              </div>
            )}
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                className="flex-1 bg-white border border-[#EDE0D4] rounded-3xl px-5 py-3.5 text-[15px] text-[#1A1A2E] placeholder:text-[#8B7355]/70 focus:outline-none focus:border-[#E07B39] focus:ring-2 focus:ring-[#E07B39]/20 resize-none font-body leading-[1.5]"
                style={{ minHeight: 52, maxHeight: 140 }}
                placeholder={c.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                rows={1}
                dir={dir}
                data-testid="chat-input"
              />
              <button
                className={`w-[52px] h-[52px] shrink-0 rounded-full flex items-center justify-center transition-all ${
                  recState === "recording"
                    ? "bg-[#E07B39] text-white saathi-mic-active shadow-[0_4px_16px_-4px_rgba(224,123,57,0.6)]"
                    : recState === "transcribing"
                    ? "bg-[#8B7355] text-white cursor-wait"
                    : "bg-white text-[#E07B39] border-2 border-[#E07B39]/40 hover:border-[#E07B39] hover:bg-[#FFF3E0]"
                }`}
                onClick={micTap}
                disabled={recState === "transcribing" || loading}
                aria-label={recState === "recording" ? "stop recording" : "record voice"}
                data-testid="mic-btn"
              >
                {recState === "recording" ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                )}
              </button>
              <button
                className={`w-[52px] h-[52px] shrink-0 rounded-full text-white text-xl flex items-center justify-center transition-all ${
                  input.trim() && !loading
                    ? "bg-[#E07B39] hover:bg-[#c96a2e] active:scale-95 shadow-[0_4px_16px_-4px_rgba(224,123,57,0.5)]"
                    : "bg-[#EDE0D4] text-[#8B7355] cursor-not-allowed"
                }`}
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                data-testid="send-btn"
                aria-label="send"
              >
                {isRTL ? "↓" : "↑"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    {modals}
    </>
  );
}
